import React, { useState, useEffect  } from "react";
import { observer } from "mobx-react-lite";
import BreadCrumbs from "../components/Breadcrumbs";
import { userInfo } from "../http/userAPI";
import {
    Container,
    Box,
    Paper,
    Tabs,
    Tab,
    Typography,
    Grid,
    Divider,
    CircularProgress
} from "@mui/material";

const UserCabinet = observer(() => {
    const [tab, setTab] = useState(0);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    const handleChange = (_, newValue) => {
        setTab(newValue);
    };

    const tabNames = {
        top_scores: 'Общий рейтинг',
        top_explosives: 'Рейдеры',
        top_resources: 'Фармеры',
        top_npc: 'Убийцы NPC',
        top_animal: 'Охотники',
        top_times: 'Время в игре',
    };

    useEffect(() => {
        const loadUserInfo = async () => {
            try {
                setLoading(true);
                const data = await userInfo();
                setUserData(data);
            } catch (e) {
                console.error("Ошибка загрузки userInfo", e);
            } finally {
                setLoading(false);
            }
        };

        loadUserInfo();
    }, []);

    const serverTitleById = React.useMemo(() => {
        if (!userData?.user_perms) return {};

        const map = {};
        Object.values(userData.user_perms).forEach((perm) => {
            map[perm.server_id] = perm.title;
        });

        return map;
    }, [userData]);

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    const OverviewTab = () => {
        const activeServicesCount = userData?.plugin_timed_perms?.length ?? 0;

        return (
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Баланс</Typography>
                        <Typography variant="h6">
                            {userData?.balance ?? 0} ₽
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Бонусы</Typography>
                        <Typography variant="h6">
                            {userData?.bonuses ?? 0}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Активные услуги</Typography>
                        <Typography variant="h6">
                            {activeServicesCount}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    const PurchasesTab = () => (
        <Box>
            <Typography variant="subtitle1" gutterBottom>
                История покупок
            </Typography>

            {userData?.store_orders?.length === 0 && (
                <Typography color="text.secondary">
                    Покупок пока нет
                </Typography>
            )}

            {userData?.store_orders?.map((order, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography fontWeight={500}>
                        {order.item_name ?? `Товар #${order.item_id}`}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {order.total_price} ₽ · {order.item_stock ?? 1} шт.
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Статус: {order.status === "delivered" ? "Доставлено" : "Можно забрать в игре"}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Сервер: {serverTitleById[order.server_id] ?? `Сервер #${order.server_id}`}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        {order.created_at}
                    </Typography>
                </Paper>
            ))}
        </Box>
    );

    const ServicesTab = () => (
        <Box>
            {userData?.plugin_timed_perms?.length === 0 && (
                <Typography color="text.secondary">
                    Активных услуг нет
                </Typography>
            )}

            {userData?.plugin_timed_perms?.map((perm, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography fontWeight={500}>
                        {perm.type.toUpperCase()}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Сервер: {serverTitleById[perm.server_id] ?? `Сервер #${perm.server_id}`}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Активен до: {perm.valid_until}
                    </Typography>
                </Paper>
            ))}
        </Box>
    );

    const RatingTab = () => {
        const groupedByServer = userData?.xdstats?.reduce((acc, stat) => {
            acc[stat.server_id] = acc[stat.server_id] || [];
            acc[stat.server_id].push(stat);
            return acc;
        }, {}) ?? {};

        return (
            <Box>
                {Object.keys(groupedByServer).length === 0 && (
                    <Typography color="text.secondary">
                        Нет данных рейтинга
                    </Typography>
                )}

                {Object.entries(groupedByServer).map(([serverId, stats]) => (
                    <Paper key={serverId} variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography fontWeight={600} sx={{ mb: 1 }}>
                            {serverTitleById[serverId] ?? `Сервер #${serverId}`}
                        </Typography>

                        {stats.map((stat, idx) => (
                            <Typography key={idx} variant="body2">
                                {tabNames[stat.category] ?? stat.category} — место #{stat.plane}, очки: {stat.score}
                            </Typography>
                        ))}
                    </Paper>
                ))}
            </Box>
        );
    };

    const ProfileTab = () => (
        <Box>
            <Typography>Ник: {userData?.name}</Typography>
            <Typography>Логин: {userData?.login}</Typography>
            <Typography>Роль: {userData?.role}</Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary">
                Здесь позже будет смена пароля, привязка Steam и безопасность
            </Typography>
        </Box>
    );






    return (
        <Container maxWidth="xl">
            {/* Breadcrumbs */}
            <Box sx={{ width: "100%", mt: 3 }}>
                <BreadCrumbs hrefs="" name="Личный кабинет" />
            </Box>

            <Box sx={{ width: "100%", mt: 3 }}>
                <Paper sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h5" fontWeight={600}>
                            Личный кабинет
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Управление аккаунтом, покупками и игровыми услугами
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Tabs */}
                    <Tabs
                        value={tab}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 3 }}
                    >
                        <Tab label="Обзор" />
                        <Tab label="Мои покупки" />
                        <Tab label="Активные услуги" />
                        <Tab label="Рейтинг" />
                        <Tab label="Профиль" />
                    </Tabs>

                    {/* Tab content */}
                    {tab === 0 && <OverviewTab />}
                    {tab === 1 && <PurchasesTab />}
                    {tab === 2 && <ServicesTab />}
                    {tab === 3 && <RatingTab />}
                    {tab === 4 && <ProfileTab />}
                </Paper>
            </Box>
        </Container>
    );
});

export default UserCabinet;
